import React from "react";
import lazyPage from "../utils/lazyPage";
import { Routes, Route, Navigate } from "react-router-dom";
const LandingPage = lazyPage(() => import("../pages/LandingPage"));
const SignupPage = lazyPage(() => import("../pages/SignupPage"));
const MerchantLoginPage = lazyPage(() => import("../pages/MerchantLoginPage"));
const GetStartedPage = lazyPage(() => import("../pages/GetStartedPage"));
const OrderHubPage = lazyPage(() => import("../pages/OrderHubPage"));
const SuperAdminLogin = lazyPage(() => import("../pages/SuperAdminLogin"));
const SuperAdminDashboard = lazyPage(() => import("../pages/SuperAdminDashboard"));
const CustomerLogin = lazyPage(() => import("../pages/CustomerLogin"));
const CustomerDashboard = lazyPage(() => import("../pages/CustomerDashboard"));
const CustomerResetPassword = lazyPage(() => import("../pages/CustomerResetPassword"));
// v4.9.1 — legacy CustomerStorefront RETIRED everywhere (was already retired on
// tenant subdomains in v4.3.1). The unified MealPassProgramStore — meal passes +
// meal plans + add-ons + menu — is THE storefront on every host.
import MealPassProgramStore from "../components/customer/mealpass/MealPassProgramStore";
// v6 - the rebuilt storefront (design system); the v5 page stays mounted at /meal-passes until every pin migrates
import Storefront from "../components/customer/storefront/Storefront";
import MenuDirectory from "../components/customer/storefront/MenuDirectory";
import MealPlansCatalogue from "../components/customer/mealpass/MealPlansCatalogue";
import StorefrontAboutPage from "../components/customer/mealpass/StorefrontAboutPage";
import StorefrontContactPage from "../components/customer/mealpass/StorefrontContactPage";
const StorefrontCheckout = lazyPage(() => import("../pages/StorefrontCheckout"));
const MealPackPayment = lazyPage(() => import("../pages/MealPackPayment"));
const QROrderPage = lazyPage(() => import("../pages/QROrderPage"));  // v5.7 — QR scan-to-order
const MarketplacePage = lazyPage(() => import("../pages/MarketplacePage"));  // Launch gate + marketplace front door
const CateringCityPage = lazyPage(() => import("../pages/CateringCityPage"));  // v5.28 directory: /catering/{city} (public, outside the launch gate)
const CateringListingPage = lazyPage(() => import("../pages/CateringListingPage"));  // v5.28 directory: /catering/{city}/{slug}
const YofoLegalPage = lazyPage(() => import("../pages/YofoLegalPage"));  // YOFO legal policies (/legal/:doc)
const OrderSuccess = lazyPage(() => import("../pages/OrderSuccess"));
const PaymentSuccess = lazyPage(() => import("../pages/PaymentSuccess"));
const ThemeEditor = lazyPage(() => import("../pages/ThemeEditor"));
const SupportCenter = lazyPage(() => import("../pages/SupportCenter"));
const PitchDeck = lazyPage(() => import("../pages/PitchDeck"));
const TenantHome = lazyPage(() => import("../pages/TenantHome"));
const DriverPortal = lazyPage(() => import("../pages/DriverPortal"));
const StaffClockPage = lazyPage(() => import("../pages/StaffClockPage"));
const KnowledgeBase = lazyPage(() => import("../pages/KnowledgeBase"));
const ShopifySSORedirect = lazyPage(() => import("../pages/ShopifySSORedirect"));
const BillingPage = lazyPage(() => import("../components/admin/BillingPage"));
import DeliveryTrackingPage from "../components/public/DeliveryTrackingPage";
import ShortTrackingRedirect from "../components/public/ShortTrackingRedirect";
const ClaimStorePage = lazyPage(() => import("../pages/ClaimStorePage"));
const PublicQuotePage = lazyPage(() => import("../pages/PublicQuotePage"));
const PublicAgreementPage = lazyPage(() => import("../pages/PublicAgreementPage"));
const FeaturesPage = lazyPage(() => import("../pages/marketing/FeaturesPage"));
const UseCasesPage = lazyPage(() => import("../pages/marketing/UseCasesPage"));
const UseCaseDetailPage = lazyPage(() => import("../pages/marketing/UseCaseDetailPage"));
const BlogIndexPage = lazyPage(() => import("../pages/marketing/BlogIndexPage"));
const BlogPostPage = lazyPage(() => import("../pages/marketing/BlogPostPage"));
const FeatureDetailPage = lazyPage(() => import("../pages/marketing/FeatureDetailPage"));
const DemoBookingPage = lazyPage(() => import("../pages/marketing/DemoBookingPage"));
import { ProtectedRoute } from "../components/auth";
import {
  SuperAdminRoute,
  MainAdminRoute,
  RedirectWithParams,
} from "../components/auth/RouteGuards";

const MainRoutes = ({ marketplaceRoot = false }) => (
  <Routes>
    {/* Landing — on the marketplace host (marketplace.plateos.io) the ROOT is the
        cross-restaurant front door; every other public route below is shared and
        works cross-host (e.g. /order?tenant=… card handoffs). */}
    <Route path="/" element={marketplaceRoot ? <MarketplacePage /> : <LandingPage />} />
    {/* Marketplace BROWSE surface — its own URL (founder). On the marketplace host
        the front door is "/" so browse is "/browse"; elsewhere it is "/marketplace/browse". */}
    <Route path="/browse" element={marketplaceRoot ? <MarketplacePage /> : <LandingPage />} />
    {/* Search-intent pages use the same honest marketplace inventory and carry
        dedicated canonical metadata. They are first-class on getyofo.com and
        remain available as /marketplace/* aliases on the shared PlateOS build. */}
    {/* v5.48 (founder): /catering is the DIRECTORY INDEX, not a second copy of
        the landing. It kept rendering MarketplacePage, so the nav's "Catering"
        navigated to the page you were already on and appeared to do nothing. */}
    <Route path="/catering" element={marketplaceRoot ? <CateringCityPage index /> : <LandingPage />} />
    <Route path="/meal-prep" element={marketplaceRoot ? <MarketplacePage /> : <LandingPage />} />
    <Route path="/restaurants-near-me" element={marketplaceRoot ? <MarketplacePage /> : <LandingPage />} />
    <Route path="/signup" element={<SignupPage />} />
    <Route path="/start/food-business" element={<LandingPage campaign />} />
    <Route path="/book-demo" element={<DemoBookingPage key="schedule" />} />
    <Route path="/demo-booked" element={<DemoBookingPage key="booked" booked />} />
    <Route path="/merchant-login" element={<MerchantLoginPage />} />
    <Route path="/get-started" element={<GetStartedPage />} />
    <Route path="/orderhub" element={<OrderHubPage />} />
    <Route path="/pitch-deck" element={<PitchDeck />} />

    {/* Public claim/review/publish page (token = capability, no auth) */}
    <Route path="/claim/:token" element={<ClaimStorePage />} />

    {/* v4.31 — public catering quote pay/review page (token = capability, no auth) */}
    <Route path="/catering/quote/:token" element={<PublicQuotePage />} />
    {/* v5.28 — the GetYOFO catering DIRECTORY (founder-approved architecture): indexed
        businesses exist here WITHOUT signing up. Mounted as its own routes, NOT under
        MarketplacePage (approved §18: "Directory pages → PUBLIC"; the marketplace's
        own launch gate was removed in v5.29). React Router ranks the static `quote` segment
        above `:city`, so the quote page above keeps its URL; `quote` is reserved as a
        city slug. */}
    <Route path="/catering/:city" element={marketplaceRoot ? <CateringCityPage /> : <LandingPage />} />
    {/* v5.31 — occasion / cuisine landing pages, declared ABOVE `:slug` so a page is
        never mistaken for a business (and `occasion`/`cuisine` are reserved slugs in
        mint_unique_slug, so no listing can ever claim one). Literal segments, not a
        `:kind` wildcard: a wildcard here would shadow every future sub-path. */}
    <Route path="/catering/:city/occasion/:landingValue" element={marketplaceRoot ? <CateringCityPage landing="occasion" /> : <LandingPage />} />
    <Route path="/catering/:city/cuisine/:landingValue" element={marketplaceRoot ? <CateringCityPage landing="cuisine" /> : <LandingPage />} />
    <Route path="/catering/:city/:slug" element={marketplaceRoot ? <CateringListingPage /> : <LandingPage />} />
    {/* v5.29 — the same pages under the /marketplace/* mirror, so the landing's address CTA
        works wherever MarketplacePage itself is mounted */}
    {/* v5.48 r1 (codex): the index needs its PREFIXED twin, or on a tenant or
        shared host the category doors navigate to /marketplace/catering, hit
        the /marketplace/* fallback, and render the landing again — the exact
        "nothing happens" this release set out to fix, one host over. */}
    <Route path="/marketplace/catering" element={<CateringCityPage index />} />
    <Route path="/marketplace/catering/:city" element={<CateringCityPage />} />
    <Route path="/marketplace/catering/:city/occasion/:landingValue" element={<CateringCityPage landing="occasion" />} />
    <Route path="/marketplace/catering/:city/cuisine/:landingValue" element={<CateringCityPage landing="cuisine" />} />
    <Route path="/marketplace/catering/:city/:slug" element={<CateringListingPage />} />

    {/* v4.31.1 — public agreement signing page (token = capability, no auth) */}
    <Route path="/agreement/:token" element={<PublicAgreementPage />} />

    {/* Marketing Pages */}
    <Route path="/features" element={<FeaturesPage />} />
    <Route path="/features/:feature" element={<FeatureDetailPage />} />
    <Route path="/use-cases" element={<UseCasesPage />} />
    <Route path="/use-cases/:segment" element={<UseCaseDetailPage />} />
    <Route path="/blog" element={<BlogIndexPage />} />
    <Route path="/blog/:slug" element={<BlogPostPage />} />

    {/* Super Admin */}
    <Route path="/superadmin/login" element={<SuperAdminRoute><SuperAdminLogin /></SuperAdminRoute>} />
    <Route path="/superadmin" element={<SuperAdminRoute><SuperAdminDashboard /></SuperAdminRoute>} />

    {/* Tenant app */}
    <Route path="/app" element={<ProtectedRoute><TenantHome /></ProtectedRoute>} />
    <Route path="/admin/*" element={<MainAdminRoute />} />

    {/* Billing */}
    <Route path="/billing" element={<ProtectedRoute><BillingPage /></ProtectedRoute>} />
    <Route path="/billing/success" element={<ProtectedRoute><BillingPage /></ProtectedRoute>} />
    <Route path="/admin/billing" element={<ProtectedRoute><BillingPage /></ProtectedRoute>} />
    <Route path="/admin/billing/success" element={<ProtectedRoute><BillingPage /></ProtectedRoute>} />

    {/* Knowledge Base (public) */}
    <Route path="/knowledge-base" element={<KnowledgeBase />} />
    <Route path="/knowledge-base/:slug" element={<KnowledgeBase />} />

    {/* Driver & Tracking */}
    <Route path="/driver/:magicToken" element={<DriverPortal />} />
    <Route path="/team" element={<StaffClockPage />} />
    <Route path="/staff-clock" element={<StaffClockPage />} />
    <Route path="/track/:trackingToken" element={<DeliveryTrackingPage />} />
    <Route path="/t/:shortCode" element={<ShortTrackingRedirect />} />

    {/* Payment */}
    <Route path="/payment-success" element={<PaymentSuccess />} />
    <Route path="/payment-cancelled" element={<PaymentSuccess />} />

    {/* Customer Portal */}
    <Route path="/customers/theme/edit" element={<ThemeEditor />} />
    <Route path="/customers/:storeSlug/theme/edit" element={<ThemeEditor />} />
    <Route path="/customers/login" element={<CustomerLogin />} />
    <Route path="/customers/checkout" element={<StorefrontCheckout />} />
    <Route path="/meal-pack-payment/:token" element={<MealPackPayment />} />
    {/* v4.9.1 — unified storefront routes (mirror TenantRoutes) */}
    <Route path="/customers/meal-passes" element={<MealPassProgramStore />} />
    <Route path="/customers/:storeSlug/meal-passes" element={<MealPassProgramStore />} />
    <Route path="/customers/plans" element={<MealPlansCatalogue />} />
    <Route path="/customers/:storeSlug/plans" element={<MealPlansCatalogue />} />
    <Route path="/customers/checkout/success" element={<OrderSuccess />} />
    <Route path="/customers/dashboard" element={<CustomerDashboard />} />
    <Route path="/customers/:storeSlug/dashboard" element={<CustomerDashboard />} />
    <Route path="/customers/reset-password" element={<CustomerResetPassword />} />
    <Route path="/customers/:storeSlug/login" element={<CustomerLogin />} />
    <Route path="/customers/:storeSlug/checkout" element={<StorefrontCheckout />} />
    <Route path="/customers/:storeSlug/checkout/success" element={<OrderSuccess />} />
    <Route path="/customers/:storeSlug/dashboard" element={<CustomerDashboard />} />
    {/* v4.25 — dedicated SEO pages (must precede the :storeSlug catch-all) */}
    <Route path="/customers/menu" element={<MenuDirectory />} />
    <Route path="/customers/menu/:itemId" element={<MenuDirectory />} />
    <Route path="/customers/:storeSlug/menu" element={<MenuDirectory />} />
    <Route path="/customers/:storeSlug/menu/:itemId" element={<MenuDirectory />} />
    <Route path="/customers/about" element={<StorefrontAboutPage />} />
    <Route path="/customers/:storeSlug/about" element={<StorefrontAboutPage />} />
    <Route path="/customers/contact" element={<StorefrontContactPage />} />
    <Route path="/customers/:storeSlug/contact" element={<StorefrontContactPage />} />
    {/* v5.7 — QR scan-to-order landing (no login); reads ?tenant=&store_slug=&table= */}
    <Route path="/order" element={<QROrderPage />} />
    <Route path="/marketplace/*" element={<MarketplacePage />} />
    <Route path="/legal/:doc" element={<YofoLegalPage />} />
    <Route path="/customers/:storeSlug" element={<Storefront />} />
    <Route path="/customers" element={<Storefront />} />

    {/* Shopify SSO redirect (from Shopify customer-account extension & Liquid block) */}
    <Route path="/shopify-sso" element={<ShopifySSORedirect />} />

    {/* Support */}
    <Route path="/support/:storeSlug" element={<SupportCenter />} />
    <Route path="/support" element={<SupportCenter />} />

    {/* /customer → /customers redirects */}
    <Route path="/customer/theme/edit" element={<Navigate to="/customers/theme/edit" replace />} />
    <Route path="/customer/login" element={<Navigate to="/customers/login" replace />} />
    <Route path="/customer/checkout/success" element={<Navigate to="/customers/checkout/success" replace />} />
    <Route path="/customer/checkout" element={<Navigate to="/customers/checkout" replace />} />
    <Route path="/customer/dashboard" element={<Navigate to="/customers/dashboard" replace />} />
    <Route path="/customer/reset-password" element={<Navigate to="/customers/reset-password" replace />} />
    <Route path="/customer/:storeSlug/theme/edit" element={<RedirectWithParams to="/customers/:storeSlug/theme/edit" />} />
    <Route path="/customer/:storeSlug/dashboard" element={<RedirectWithParams to="/customers/:storeSlug/dashboard" />} />
    <Route path="/customer/:storeSlug/login" element={<RedirectWithParams to="/customers/:storeSlug/login" />} />
    <Route path="/customer/:storeSlug/checkout/success" element={<RedirectWithParams to="/customers/:storeSlug/checkout/success" />} />
    <Route path="/customer/:storeSlug/checkout" element={<RedirectWithParams to="/customers/:storeSlug/checkout" />} />
    <Route path="/customer/:storeSlug" element={<RedirectWithParams to="/customers/:storeSlug" />} />
    <Route path="/customer" element={<Navigate to="/customers" replace />} />

    {/* Auth fallback */}
    <Route path="/login" element={<LandingPage />} />
  </Routes>
);

export default MainRoutes;
